import { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import useAppStore from '../context/AppContext';
import { uploadFiles, deleteFile, getModels } from '../services/api';
import FileCard from '../components/FileCard';

const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'text/csv': ['.csv'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILES = 14;

function Home() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const {
    uploadedFiles,
    addFiles,
    removeFile,
    clearFiles,
    availableModels,
    selectedModel,
    setAvailableModels,
    setSelectedModel,
    getSelectedModel,
    availableVerificationModels,
    setAvailableVerificationModels,
    setSelectedVerificationModel,
    getSelectedVerificationModel,
  } = useAppStore();

  // Fetch available models on mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const result = await getModels();
        if (result.success) {
          setAvailableModels(result.models, result.defaultModel);
          // Also set verification models
          if (result.verificationModels) {
            setAvailableVerificationModels(result.verificationModels, result.defaultVerificationModel);
          }
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
      }
    };
    fetchModels();
  }, [setAvailableModels, setAvailableVerificationModels]);

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      // Handle rejected files
      rejectedFiles.forEach((rejection) => {
        const { file, errors } = rejection;
        errors.forEach((error) => {
          let message = `${file.name}: `;
          if (error.code === 'file-too-large') {
            message += 'File is too large (max 50MB)';
          } else if (error.code === 'file-invalid-type') {
            message += 'Invalid file type (PDF, CSV, Excel, or images only)';
          } else {
            message += error.message;
          }
          enqueueSnackbar(message, { variant: 'error' });
        });
      });

      if (acceptedFiles.length === 0) return;

      // Check total file count
      if (uploadedFiles.length + acceptedFiles.length > MAX_FILES) {
        enqueueSnackbar(`Maximum ${MAX_FILES} files allowed`, { variant: 'warning' });
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);

      try {
        const result = await uploadFiles(acceptedFiles, (percent) => {
          setUploadProgress(percent);
        });

        // Backend returns { success, data: { files, count } }
        if (result.success && result.data?.files) {
          addFiles(result.data.files);
          enqueueSnackbar(
            `${result.data.files.length} file(s) uploaded successfully`,
            { variant: 'success' }
          );
        }
      } catch (error) {
        console.error('Upload error:', error);
        enqueueSnackbar(
          error.response?.data?.error?.message || error.response?.data?.error || 'Failed to upload files',
          { variant: 'error' }
        );
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [addFiles, enqueueSnackbar, uploadedFiles.length]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES,
    disabled: isUploading,
  });

  const handleRemoveFile = async (fileId) => {
    const file = uploadedFiles.find((f) => f.id === fileId);
    if (file?.serverFilename) {
      try {
        await deleteFile(file.serverFilename);
      } catch (error) {
        console.warn('Failed to delete file from server:', error);
      }
    }
    removeFile(fileId);
    enqueueSnackbar('File removed', { variant: 'info' });
  };

  const handleClearAll = async () => {
    // Delete all files from server
    for (const file of uploadedFiles) {
      if (file.serverFilename) {
        try {
          await deleteFile(file.serverFilename);
        } catch (error) {
          console.warn('Failed to delete file:', error);
        }
      }
    }
    clearFiles();
    enqueueSnackbar('All files cleared', { variant: 'info' });
  };

  const handleProcess = () => {
    if (uploadedFiles.length === 0) {
      enqueueSnackbar('Please upload at least one file', { variant: 'warning' });
      return;
    }
    navigate('/processing');
  };

  const currentModel = getSelectedModel();
  const currentVerificationModel = getSelectedVerificationModel();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography variant="h4" gutterBottom>
            Upload Bank Statements
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Drag and drop your bank or credit card statements (PDF, CSV, Excel, or images)
            to convert them to QuickBooks Online format.
          </Typography>
        </Box>

        {/* Model Selectors */}
        {availableModels.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Primary Model (Pass 1) */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="model-select-label">Pass 1 Model</InputLabel>
              <Select
                labelId="model-select-label"
                value={currentModel}
                label="Pass 1 Model"
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                {availableModels.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{model.name}</span>
                      {model.cost && (
                        <Chip
                          label={model.cost}
                          size="small"
                          color={model.cost === '$' ? 'success' : model.cost === '$$' ? 'warning' : 'error'}
                          sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Verification Model (Pass 2) */}
            {availableVerificationModels.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="verification-model-select-label">Pass 2 Model</InputLabel>
                <Select
                  labelId="verification-model-select-label"
                  value={currentVerificationModel}
                  label="Pass 2 Model"
                  onChange={(e) => setSelectedVerificationModel(e.target.value)}
                >
                  {availableVerificationModels.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{model.name}</span>
                        {model.cost && (
                          <Chip
                            label={model.cost}
                            size="small"
                            color={model.cost === '$' ? 'success' : model.cost === '$$' ? 'warning' : 'error'}
                            sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </Box>

      {/* Dropzone */}
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          mb: 3,
          textAlign: 'center',
          cursor: isUploading ? 'not-allowed' : 'pointer',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        {isUploading ? (
          <Box>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6">Uploading... {uploadProgress}%</Typography>
          </Box>
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to browse
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
              Supported: PDF, CSV, Excel (.xlsx, .xls), Images (PNG, JPG) • Max 50MB per file • Up to {MAX_FILES} files
            </Typography>
          </>
        )}
      </Paper>

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Uploaded Files ({uploadedFiles.length}/{MAX_FILES})
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteSweepIcon />}
              onClick={handleClearAll}
            >
              Clear All
            </Button>
          </Box>

          {uploadedFiles.map((file) => (
            <FileCard
              key={file.id}
              file={file}
              onRemove={handleRemoveFile}
            />
          ))}

          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            {uploadedFiles.length === 1
              ? '1 file ready for processing'
              : `${uploadedFiles.length} files ready for processing`}
          </Alert>

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<PlayArrowIcon />}
            onClick={handleProcess}
            sx={{ mt: 1 }}
          >
            Process Files with AI
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default Home;
